// Mirror of functions/accounting/fixedAssetLedger.ts; update with node scripts/sync-fixed-asset-domain.mjs.
const fixedAssetLedgerApi = (() => {
// Shared, database-free asset identity and manual-journal projection.
const assetRows = (value: any): any[] => Array.isArray(value) ? value : []
const assetMoney = (value: any) => Math.round((Number(value) || 0) * 100) / 100

const withFixedAssetNumbers = (state: any) => {
  const records = assetRows(state.expenses).flatMap(expense => [
    ...(expense.fixedAsset ? [{ id: expense.id, fixed: expense.fixedAsset }] : []),
    ...assetRows(expense.purchaseLines).filter(line => line.fixedAsset).map(line => ({ id: `${expense.id}:${line.id}`, fixed: line.fixedAsset })),
  ]).concat(assetRows(state.accountingImportFixedAssets).map(fixed => ({ id: fixed.id, fixed })))
  if (!records.length || records.every(row => row.fixed.assetNumber)) return state
  let next = Math.max(10000, ...records.map(row => Number(row.fixed.assetNumber) || 0))
  const assigned = new Map(records.sort((a, b) => String(a.id).localeCompare(String(b.id))).map(row => [row.id, row.fixed.assetNumber || String(++next)]))
  const fixed = (id: string, value: any) => value ? { ...value, assetNumber: assigned.get(id) } : value
  return { ...state,
    expenses: assetRows(state.expenses).map(expense => ({ ...expense,
      ...(expense.fixedAsset ? { fixedAsset: fixed(expense.id, expense.fixedAsset) } : {}),
      ...(Array.isArray(expense.purchaseLines) ? { purchaseLines: expense.purchaseLines.map((line: any) => ({ ...line, ...(line.fixedAsset ? { fixedAsset: fixed(`${expense.id}:${line.id}`, line.fixedAsset) } : {}) })) } : {}),
    })),
    accountingImportFixedAssets: assetRows(state.accountingImportFixedAssets).map(row => fixed(row.id, row)),
  }
}

// Original and reversal keep their own accounting dates. Never infer an asset
// from a control-account balance or from the document description.
const fixedAssetManualRows = (state: any, assetId?: string, cutoff = '9999-12-31') => assetRows(state.expenses)
  .filter(entry => entry.manualPosting && !['draft', 'deleted'].includes(entry.status))
  .flatMap(entry => assetRows(entry.manualPostingLines).flatMap((line, index) => ['from', 'to'].flatMap(side => {
    const id = String(line[`${side}AssetId`] || '')
    if (!id || (assetId && id !== assetId)) return []
    const row = { id: `${entry.id}:${line.id || index}:${side}`, sourceId: entry.id, lineId: line.id, side, assetId: id,
      account: String(line[`${side}Account`] || '').split(':')[0], effect: line[`${side}AssetEffect`],
      amount: assetMoney(Number(line[`${side}Amount`]) * (side === 'from' ? -1 : 1)), date: entry.date,
      description: entry.description, voucherNumber: entry.voucherNumber, createdAt: entry.createdAt, reversal: false }
    const reversal = entry.status === 'annulled' && entry.reversalDate
      ? [{ ...row, id: `${row.id}:reversal`, date: entry.reversalDate, amount: -row.amount, reversal: true }] : []
    return [row, ...reversal].filter(item => item.date <= cutoff)
  })))

const fixedAssetManualMovements = (state: any, asset: any, account: string, accumulated: string) => fixedAssetManualRows(state, asset.id)
  .filter(row => row.account === account || row.account === accumulated)
  .filter(row => row.effect !== 'depreciation')
  .map(row => ({ id: row.id, date: row.date, sourceExpenseId: row.sourceId, note: row.description, manualLedger: true,
    type: row.effect === 'cost_adjustment' ? (row.amount >= 0 ? 'improvement' : 'cost_reduction') : 'ledger_adjustment',
    amount: row.effect === 'cost_adjustment' ? Math.abs(row.amount) : row.amount, taxAmount: 0 }))

const fixedAssetLastManualDepreciationDate = (state: any, assetId: string, account: string) => {
  const totals = new Map<string, number>()
  for (const row of fixedAssetManualRows(state, assetId).filter(row => row.effect === 'depreciation' && row.account === account)) totals.set(row.date, (totals.get(row.date) || 0) - row.amount)
  return [...totals.entries()].filter(([, value]) => value > .005).map(([date]) => date).sort().at(-1)
}

// Display numbers are server-owned metadata, including when a purchase is copied.
const preservePurchaseAssetNumbers = (expense: any, previous: any) => {
  const preserve = (fixed: any, old: any) => {
    if (!fixed) return
    if (old?.assetNumber) fixed.assetNumber = old.assetNumber
    else delete fixed.assetNumber
  }
  preserve(expense.fixedAsset, previous?.fixedAsset)
  for (const line of assetRows(expense.purchaseLines)) preserve(line.fixedAsset, assetRows(previous?.purchaseLines).find(row => row.id === line.id)?.fixedAsset)
}

return { numbered: withFixedAssetNumbers, manualRows: fixedAssetManualRows, manualMovements: fixedAssetManualMovements }
})();
window.PlummeFixedAssetLedger = fixedAssetLedgerApi
